import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, Dropdown, Input, Message } from 'semantic-ui-react';
import { getWithAuth } from 'src/api';
import { coordSniffer, parseCoords } from 'src/pages/map/SiteModePanel';
import { store } from 'src/store';
import { getCanvas } from 'src/utils/mapUtils';
import { encodeLatLong, isLatorLong } from 'src/utils/useful_functions';
import config from 'src/config';

// don't run autocomplete until we have at least 5 characters in the box
const MIN_AUTOCOMPLETE_LENGTH = 5;

const showError = (errorMessage='', timeout=5000) => {
  const userLimits = store.getState().mainFrame.userLimits;
  const maxSearchesPerHour = userLimits?.address_search || 0;
  const errMsg =
    errorMessage ||
    `Too many searches. Your limit is ${maxSearchesPerHour} per hour.
    Contact linkplanner@cambiumnetworks.com to discuss increasing your limit.`;
  toast(<Message error>{errMsg}</Message>, {
    autoClose: timeout,
  });
};

const autocomplete = (searchText, setAutocompleteOptions) => {
  const trimmedText = searchText.trim();
  setAutocompleteOptions(null);
  if (
    coordSniffer(trimmedText) ||
    trimmedText.split(',').every((txt) => isLatorLong(txt.trim()))
  ) {
    // if the user is still typing coordinates, but they
    // aren't currently valid then do nothing...
  } else if (trimmedText.length >= MIN_AUTOCOMPLETE_LENGTH) {
    getWithAuth(`autocomplete?address=${encodeURIComponent(trimmedText)}`)
      .then((res) => {
        if (res.status === 'success') {
          setAutocompleteOptions(res.place_names);
        } else {
          console.error('Unexpected autocomplete error');
          showError(res?.message || 'Unknown error', 2000);
        }
      })
      .catch((reason) => {
        console.error(reason);
        showError();
      });
  }
};

const doSearch = (searchText) => {
  const trimmedText = searchText.trim();
  if (trimmedText) {
    const { lat, lng, valid } = parseCoords(trimmedText);
    const canvas = getCanvas();
    if (valid) {
      // location is coordinates so show in the map
      canvas.setSearchCoords(lat, lng);
    } else if (coordSniffer(trimmedText)) {
      // if it "smells" like a coordinate string then do nothing
      // and allow the user to keep typing
    } else {
      canvas.setSearchCoords(null, null);
      getWithAuth(`address?address=${encodeURIComponent(trimmedText)}`)
        .then((res) => {
          if (res.status === 'success') {
            if (res.locations) {
              const coords = Object.values(res.locations);
              if (coords.length > 0) {
                const { lat, lng } = coords[0];
                const canvas = getCanvas();
                canvas.setSearchCoords(lat, lng);
              }
            }
          } else {
            showError(res?.message || 'Unknown error', 2000);
          }
        })
        .catch((reason) => {
          console.error(reason);
          showError();
        });
    }
  }
};

export const AddressSearch = () => {
  const [showInput, setShowInput] = useState(false);
  const [lastAddress, setLastAddress] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState(null);
  const [googleSearchKeyAvailable, setGoogleSearchKeyAvailable] = useState('');

  const searchCoords = useSelector((state) => state.map.searchCoords);
  const prefs = useSelector((state) => state.mainFrame.prefs);
  const { latLngFormat } = prefs;
  const searchWidth = '40vw';

  // Fetch config on component mount
  useEffect(() => {
    getWithAuth(`config/google_keys`)
      .then((res) => {
        if (res?.google_search_key_available) {
          setGoogleSearchKeyAvailable(res.google_search_key_available);
        }
      })
      .catch((reason) => {
        console.error(reason);
      });
  }, []);

  useEffect(() => {
    if (searchCoords.lat != null) {
      setLastAddress(
        `${encodeLatLong(
          searchCoords.lat,
          latLngFormat,
          true
        )}, ${encodeLatLong(searchCoords.lng, latLngFormat, false)}`
      );
    } else {
      setLastAddress('');
      setAutocompleteOptions(null);
    }
  }, [searchCoords, prefs]);

  // Don't render if Google Search API Key is not available
  if (!googleSearchKeyAvailable) {
    return null;
  }

  const baseStyle = {
    position: 'absolute',
    left: '10px',
    top: '10px',
  };

  if (showInput) {
    return (
      <>
        <Input
          placeholder="Address or coordinates (latitude, longitude)..."
          style={{
            ...baseStyle,
            width: searchWidth,
          }}
          size="massive"
          autoFocus
          onFocus={(e) => e.target.select()}
          value={lastAddress}
          onChange={(e, data) => {
            setLastAddress(data.value);
            autocomplete(data.value, setAutocompleteOptions);
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              let searchAddress = lastAddress;
              if (autocompleteOptions && autocompleteOptions.length > 0) {
                // Use the first autocomplete result so that the
                // search gives the expected location
                searchAddress = autocompleteOptions[0];
              }
              doSearch(searchAddress);
            }
          }}
        >
          <Button
            icon="search"
            size="massive"
            color="blue"
            title="Search"
            onClick={() => doSearch(lastAddress)}
          />
          <input
            style={{ padding: 0, paddingLeft: '4px', fontSize: '0.85em' }}
          />
          <Button
            icon="close"
            size="massive"
            title="Close"
            onClick={() => {
              setShowInput(!showInput);
              setAutocompleteOptions(null);
              getCanvas().removeSearchMarker();
            }}
          />
        </Input>
        {autocompleteOptions && autocompleteOptions.length > 0 && (
          <Dropdown
            style={{
              position: 'absolute',
              left: '10px',
              top: 'calc(10px + 4em)',
              width: searchWidth,
            }}
            open
            fluid
            floating
            selection
            options={autocompleteOptions.map((key) => {
              return { key: key, value: key, text: key };
            })}
            onChange={(e, data) => {
              const { value } = data;
              doSearch(value);
            }}
          />
        )}
      </>
    );
  } else {
    return (
      <Button
        icon="search"
        size="massive"
        color="blue"
        style={baseStyle}
        title="Search for a location using an address or coordinates"
        onClick={() => setShowInput(!showInput)}
        // onMouseOver={() => setShowInput(true)}
      />
    );
  }
};
